import React from "react";
import { LayoutDashboard, Plus, Settings, LogOut, History, ClipboardList, MessageSquare, Link2, Lock } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, canAccess } from "@/hooks/useSubscription";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

const mainNavItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Previous Analyses", url: "/analyses", icon: History },
  { title: "Action Plan", url: "/action-plan", icon: ClipboardList },
  { title: "AI Assistant", url: "/chat", icon: MessageSquare },
  { title: "Backlink Opportunities", url: "/backlink-opportunities", icon: Link2 },
];

interface DashboardSidebarProps {
  onNewAnalysis: () => void;
}

export function DashboardSidebar({ onNewAnalysis }: DashboardSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { plan } = useSubscription();

  const isGrowthLocked = (url: string) =>
    (url === "/chat" && !canAccess(plan, "aiChat")) ||
    (url === "/backlink-opportunities" && !canAccess(plan, "backlinkOpportunities"));

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        {!collapsed ? (
          <img
            src="/Unranked-agency-logo.png"
            alt="Unranked Agency"
            className="h-8 object-contain"
          />
        ) : (
          <img
            src="/Unranked-agency-logo.png"
            alt="Unranked"
            className="h-8 w-8 object-contain"
          />
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item, index) => (
                <React.Fragment key={item.title}>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                      <NavLink
                        to={item.url}
                        end
                        className="hover:bg-muted/50"
                        activeClassName="bg-muted text-primary font-medium"
                      >
                        {isGrowthLocked(item.url) ? (
                          <Lock className="mr-2 h-4 w-4 text-muted-foreground" />
                        ) : (
                          <item.icon className="mr-2 h-4 w-4" />
                        )}
                        {!collapsed && (
                          <span className="flex items-center gap-2">
                            {item.title}
                            {isGrowthLocked(item.url) && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Growth</Badge>
                            )}
                          </span>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {index === 0 && (
                    <SidebarMenuItem>
                      <SidebarMenuButton onClick={onNewAnalysis} className="hover:bg-muted/50">
                        <Plus className="mr-2 h-4 w-4" />
                        {!collapsed && <span>New Analysis</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </React.Fragment>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/settings")}>
              <NavLink
                to="/settings"
                end
                className="hover:bg-muted/50"
                activeClassName="bg-muted text-primary font-medium"
              >
                <Settings className="mr-2 h-4 w-4" />
                {!collapsed && <span>Settings</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="hover:bg-muted/50 text-muted-foreground hover:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              {!collapsed && <span>Logout</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
